import React, { DependencyList, useMemo, useState } from 'react'
import { NavLink, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Action } from '../../../types/actionTypes'
import { formatTitle } from '../../../utils/formatTitle'
import {
  DetailAccordion,
  DetailEntityPanel,
  DetailTable,
} from './DetailPrimitives'
import { DetailRowActions } from './DetailRowActions'
import {
  ListPageDateCell,
  ListPagePill,
  PRIORITY_PILL,
  STATUS_PILL,
} from '../ListPageCells'
import { ListPageDescription } from '../ListPageDescription'
import { priorityLabelsTranslations } from '../../../utils/constants'
import { useTableLoadingGate } from '../../../hooks/useTableLoadingGate'
import i18n from '../../../i18n'
import '../ListPageTable.css'

type Props = {
  title: string
  listTitle: string
  emptyMessage: string
  rows: Action[]
  loading?: boolean
  /** Fetch identity deps so the loader re-arms when the parent entity changes. */
  loadingDeps?: DependencyList
  roles: string[]
  open?: boolean
  onToggle?: () => void
  onDelete?: (id: string) => void
}

/** Related corrective/actions table used on Audit / Incident / Risk detail pages. */
export function RelatedActionsPanel({
  title,
  listTitle,
  emptyMessage,
  rows,
  loading,
  loadingDeps = [],
  roles,
  open = true,
  onToggle,
  onDelete,
}: Props) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [search, setSearch] = useState('')
  const locale = i18n.language
  const showLoader = useTableLoadingGate(!!loading, loadingDeps)

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return rows || []
    return (rows || []).filter((a) =>
      (a.ActionName || '').toLowerCase().includes(q)
    )
  }, [rows, search])

  const body = (
    <DetailEntityPanel
      listTitle={listTitle}
      searchPlaceholder={t('filterByName', { defaultValue: 'Filter by name' })}
      searchValue={search}
      onSearchChange={setSearch}
      canAdd={false}
    >
      <DetailTable<Action>
        loading={showLoader && !(rows && rows.length)}
        emptyTitle={t('missionDetailsPage.empty.title', {
          defaultValue: 'Nothing here yet',
        })}
        emptyMessage={emptyMessage}
        rows={filtered}
        rowKey={(r) => r._id as string}
        columns={[
          {
            key: 'name',
            header: t('task_name', { defaultValue: 'Task name' }),
            render: (r) => (
              <NavLink to={`/actiond/${r._id}`} className="incyia-name-link">
                <ListPageDescription
                  value={
                    r.ActionName ? formatTitle(r.ActionName) : undefined
                  }
                  tone="primary"
                />
              </NavLink>
            ),
          },
          {
            key: 'priority',
            header: t('Priority'),
            render: (r) => {
              const priority = Number(r.Priority)
              const style = PRIORITY_PILL[priority] ?? PRIORITY_PILL[4]
              return (
                <ListPagePill
                  label={
                    t(`priority.${priority}`, {
                      defaultValue:
                        priorityLabelsTranslations[priority] || style.label,
                    }) || style.label
                  }
                  bg={style.bg}
                  color={style.color}
                />
              )
            },
          },
          {
            key: 'status',
            header: t('Status'),
            render: (r) => {
              const style = STATUS_PILL[r.Status || ''] ?? {
                bg: '#EAF0F0',
                color: '#5F7484',
              }
              return (
                <ListPagePill
                  label={t(r.Status || '', {
                    defaultValue: r.Status || '—',
                  })}
                  bg={style.bg}
                  color={style.color}
                />
              )
            },
          },
          {
            key: 'end',
            header: t('End Date', { defaultValue: 'End date' }),
            render: (r) => (
              <ListPageDateCell value={r.DateFin} locale={locale} />
            ),
          },
        ]}
        actionsHeader={t('Actions', { defaultValue: 'Action' })}
        renderActions={(r) => (
          <DetailRowActions
            roles={roles}
            onEdit={() => navigate(`/action/${r._id}`)}
            onDelete={
              onDelete ? () => onDelete(r._id as string) : undefined
            }
          />
        )}
      />
    </DetailEntityPanel>
  )

  if (!onToggle) return body

  return (
    <DetailAccordion
      title={title}
      count={(rows || []).length}
      open={open}
      onToggle={onToggle}
    >
      {body}
    </DetailAccordion>
  )
}
